/**
 * The last stretch of the quiet way in (03 §5.2). The chain of ravines carries a crawling ranger
 * unseen from the drop band to the far side of the belt; from there to the goal zone's edge he
 * is in the open. The generator picks that stretch - straight north from the ravine's end, then
 * along to a point on the goal zone's quiet side that nobody already posted is looking at -
 * keeps it clear, and from then on refuses to post a sentry or site a gun that would overlook it.
 * Patrols and lamps may: timing them is the player's business, and the validator's.
 *
 * "Overlook" is asked of the validator's own model of sight (validate/quiet), over the props
 * laid so far - anything added later can only hide more.
 */

import { collidersFor } from '../sim/props';
import type { PropKind, SpawnDef } from '../sim/types';
import { FLAG } from './grid';
import { QUIET } from './tuning';
import type { GenCtx, Point } from './types';
import { type GarrisonView, lampSweep, watcherOf } from './validate/quiet/model';
import { SightIndex, trenchCells } from './validate/quiet/sight';

const SHUT = FLAG.solid | FLAG.mine;

/** Cells from `from` to `to`: north (or south) first and then along, or the other way about. Null if any is shut. */
function legs(ctx: GenCtx, from: Point, to: Point, northFirst: boolean): number[] | null {
  const width = ctx.map.mask.width;
  const [fx, fz, tx, tz] = [Math.floor(from.x), Math.floor(from.z), Math.floor(to.x), Math.floor(to.z)];
  const cells: number[] = [];
  const walk = (x0: number, z0: number, x1: number, z1: number): boolean => {
    const steps = Math.abs(x1 - x0) + Math.abs(z1 - z0);
    for (let s = 0; s <= steps; s++) {
      const x = x0 + Math.sign(x1 - x0) * s;
      const z = z0 + Math.sign(z1 - z0) * s;
      if ((ctx.map.mask.at(x + 0.5, z + 0.5) & SHUT) !== 0) return false;
      cells.push(z * width + x);
    }
    return true;
  };
  const ok = northFirst ? walk(fx, fz, fx, tz) && walk(fx, tz, tx, tz) : walk(fx, fz, tx, fz) && walk(tx, fz, tx, tz);
  return ok ? cells : null;
}

/** Where on the goal zone's edge the quiet way in might end, nearest the ravine first. */
function landings(ctx: GenCtx, end: Point): Array<{ at: Point; northFirst: boolean }> {
  const goal = ctx.bands[ctx.bands.length - 1];
  if (ctx.spec.layout === 'border') return goal ? [{ at: { x: end.x, z: goal.z1 - 2 }, northFirst: true }] : [];
  const c = ctx.plan.compound;
  const out = QUIET.goalMargin - 0.5;
  const under = end.x > c.x0 - out && end.x < c.x1 + out;
  const flankX = ctx.plan.roadEast ? c.x0 - out : c.x1 + out;
  const inward = ctx.plan.roadEast ? 1 : -1;
  const list: Array<{ at: Point; northFirst: boolean }> = [];
  // Straight up to the south side if the ravine ends under it; the quiet corner; along the south side; up the flank.
  if (under) list.push({ at: { x: end.x, z: c.z1 + out }, northFirst: true });
  list.push({ at: { x: flankX, z: c.z1 + out }, northFirst: !under });
  for (const step of [5, 10, 15]) list.push({ at: { x: flankX + inward * step, z: c.z1 + out }, northFirst: !under });
  for (const step of [5, 10]) list.push({ at: { x: flankX, z: c.z1 - step }, northFirst: !under });
  return list;
}

/** Choose the open stretch and reserve it: once the goal is built (its guards count), before the budget is spent. */
export function planQuietApproach(ctx: GenCtx): void {
  const { map } = ctx;
  const { width, height } = map.mask;
  const trench = trenchCells(map.trenches, width, height);
  const sight = new SightIndex(map.props.flatMap((p) => collidersFor(p)), width, height, trench);
  ctx.quiet.sight = sight;
  const end = ctx.quiet.end;
  if (!end) return;

  const view: GarrisonView = {
    timeOfDay: ctx.params.timeOfDay, props: map.props, inTrench: (x, z) => sight.inTrench(x, z),
  };
  const posted = map.spawns
    .filter((s) => s.dormantUntil === undefined && (s.patrol === undefined || s.patrol.length < 2))
    .map((s) => watcherOf(view, s))
    .filter((w) => w !== null);
  let best: number[] | null = null;
  let bestSeen = Infinity;
  for (const landing of landings(ctx, end)) {
    const line = legs(ctx, end, landing.at, landing.northFirst);
    if (!line) continue;
    // Three cells wide, so a rock dropped beside it later cannot close it; ravine cells need no protecting.
    const open = new Set<number>();
    for (const cell of line) {
      for (const n of [cell, cell - 1, cell + 1, cell - width, cell + width]) {
        if (n < 0 || n >= trench.length || trench[n] !== 0) continue;
        const x = n % width;
        if ((map.mask.at(x + 0.5, (n - x) / width + 0.5) & SHUT) === 0) open.add(n);
      }
    }
    const cells = [...open];
    const seen = posted.filter((w) => sight.seesAny(w, cells)).length;
    if (seen < bestSeen) {
      best = cells;
      bestSeen = seen;
    }
    if (seen === 0) break;
  }
  if (!best) return;
  // Ground the goal itself asked to have left unwatched (a prisoner's way out of the camp, mission 32) stays protected.
  ctx.quiet.exposed = [...ctx.quiet.exposed, ...best];
  for (const cell of best) {
    const x = cell % width;
    map.mask.rect(x, (cell - x) / width, x + 0.5, (cell - x) / width + 0.5, FLAG.keep);
  }
}

/** Stands in for the id of an emplacement that is not built yet. */
const UNBUILT = -2;

/**
 * Would a man who holds this post see a ranger crawling the open stretch? `gun` is the emplacement
 * he is about to be given, when it is not on the map yet: a turret bunker sees all round.
 */
export function overlooksQuietApproach(ctx: GenCtx, s: SpawnDef, gun?: PropKind): boolean {
  const { sight, exposed } = ctx.quiet;
  if (!sight || exposed.length === 0) return false;
  const props = gun ? [{ id: UNBUILT, kind: gun, x: s.x, z: s.z, yaw: 0 }] : ctx.map.props;
  const view: GarrisonView = { timeOfDay: ctx.params.timeOfDay, props, inTrench: (x, z) => sight.inTrench(x, z) };
  const watcher = watcherOf(view, gun ? { ...s, mans: UNBUILT } : s);
  return watcher !== null && sight.seesAny(watcher, exposed);
}

/** Would a searchlight here (facing south, as they all do) ever light the open stretch? */
export function lampOverlooksQuietApproach(ctx: GenCtx, x: number, z: number): boolean {
  const { sight, exposed } = ctx.quiet;
  return sight !== null && exposed.length > 0 && sight.seesAny(lampSweep({ id: UNBUILT, x, z, yaw: 0 }), exposed);
}
